import React, { useEffect, useMemo, useState } from "react";
import { parseNaturalSplitTransactionText } from "../../shared/utils/parseNaturalSplitTransaction";
import { formatAmountCell } from "../../shared/utils/financeDisplay";
import { getMessageForFetchError } from "../../shared/utils/apiError";
import { splitTransaction } from "../../shared/api/transactions";

function getSignedSplitAmount(originalAmount, splitMagnitude) {
  const originalNum = Number(originalAmount);
  const mag = Number(splitMagnitude);
  if (!Number.isFinite(originalNum) || !Number.isFinite(mag) || mag <= 0) return null;
  const sign = originalNum < 0 ? -1 : 1;
  return sign * Math.abs(mag);
}

/**
 * Split Transaction (NLP) modal
 *
 * Props:
 * - open: boolean
 * - accountId: string
 * - transaction: object | null
 * - onClose: () => void
 * - onSaved: () => void
 */
export default function SplitTransactionModal({ open, accountId, transaction, onClose, onSaved }) {
  const [naturalText, setNaturalText] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setNaturalText("");
      setPreviewText("");
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  const parsed = useMemo(() => parseNaturalSplitTransactionText(previewText || ""), [previewText]);

  const preview = useMemo(() => {
    const originalAmount = Number(transaction?.amount);
    if (!transaction || !Number.isFinite(originalAmount)) return null;
    if (!previewText) return null;
    if (parsed.amount == null) return null;
    const signedSplit = getSignedSplitAmount(originalAmount, parsed.amount);
    if (signedSplit == null) return null;
    const newOriginal = originalAmount - signedSplit;
    return {
      signedSplit,
      newOriginal,
      splitName: (parsed.name || "").trim(),
      valid:
        Boolean((parsed.name || "").trim()) &&
        Math.abs(signedSplit) > 0 &&
        Math.abs(signedSplit) < Math.abs(originalAmount) &&
        Number.isFinite(newOriginal),
    };
  }, [transaction, previewText, parsed.amount, parsed.name]);

  if (!open) return null;

  const originalAmountNum = Number(transaction?.amount);
  const originalName = transaction?.name || "—";

  function handlePreview() {
    const trimmed = (naturalText || "").trim();
    if (!trimmed) {
      setError("Enter a split instruction like: Split $50 to Late Fee");
      setPreviewText("");
      return;
    }
    setError("");
    setPreviewText(trimmed);
  }

  async function handleSave() {
    if (!accountId) {
      setError("Missing account information.");
      return;
    }
    if (!transaction?._id) {
      setError("Missing transaction.");
      return;
    }
    if (!preview) {
      setError("Click Preview (or press Enter) to review the split before saving.");
      return;
    }
    if (!preview.valid) {
      if (!preview.splitName) {
        setError("Please include a name for the new split row (e.g. 'Late Fee').");
        return;
      }
      if (!Number.isFinite(originalAmountNum) || Math.abs(originalAmountNum) <= 0) {
        setError("Cannot split a transaction with a zero amount.");
        return;
      }
      if (!(Math.abs(preview.signedSplit) < Math.abs(originalAmountNum))) {
        setError("Split amount must be less than the original amount.");
        return;
      }
      setError("Split preview is invalid.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await splitTransaction({
        account_id: accountId,
        _id: transaction._id,
        splitAmount: Math.abs(preview.signedSplit),
        splitName: preview.splitName,
      });
      if (onSaved) onSaved();
      if (onClose) onClose();
    } catch (err) {
      setError(getMessageForFetchError(err, "Unable to split transaction."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
        padding: "12px",
        boxSizing: "border-box",
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="split-transaction-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose && !submitting) onClose();
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          maxWidth: "520px",
          width: "100%",
          maxHeight: "calc(100vh - 24px)",
          overflowY: "auto",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
          padding: "18px 20px 16px",
          boxSizing: "border-box",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <h3 id="split-transaction-modal-title" style={{ margin: 0, fontSize: "1rem", fontWeight: 600, color: "#212529", flex: "1 1 auto" }}>
            Split Transaction (NLP)
          </h3>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              aria-label="Close"
              style={{
                border: "none",
                background: "none",
                cursor: submitting ? "not-allowed" : "pointer",
                fontSize: "18px",
                lineHeight: 1,
                padding: "2px 4px",
                color: "#6c757d",
              }}
            >
              ×
            </button>
          )}
        </div>

        <p style={{ margin: "8px 0", fontSize: 13, color: "#6c757d", overflowWrap: "anywhere" }}>
          Example: <span style={{ fontFamily: "monospace" }}>Split $50 to Late Fee</span>
        </p>

        <div style={{ marginTop: 8, padding: "10px 12px", border: "1px solid #dee2e6", borderRadius: 6, background: "#f8f9fa" }}>
          <div style={{ fontSize: 12, color: "#57606a", marginBottom: 6 }}>Original</div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>{originalName}</div>
            <div className="tabular-nums" style={{ whiteSpace: "nowrap" }}>{formatAmountCell(originalAmountNum)}</div>
          </div>
        </div>

        {error && (
          <div style={{ fontSize: 13, color: "#b02a37", marginTop: 10, padding: "6px 8px", background: "#f8d7da", border: "1px solid #f5c2c7", borderRadius: 4 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
          <input
            type="text"
            value={naturalText}
            onChange={(e) => {
              setNaturalText(e.target.value);
              if (previewText) setPreviewText("");
              if (error) setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handlePreview();
              }
            }}
            placeholder="Split $50 to Late Fee"
            disabled={submitting}
            style={{
              width: "100%",
              minWidth: 0,
              padding: "6px 8px",
              fontSize: 13,
              borderRadius: 4,
              border: "1px solid #ced4da",
              boxSizing: "border-box",
            }}
          />
        </div>

        {preview && (
          <div style={{ marginTop: 10, padding: "10px 12px", background: "#e7f1ff", border: "1px solid #b6d4fe", borderRadius: 6, fontSize: 13 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Preview</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "6px 12px", alignItems: "baseline" }}>
              <div style={{ color: "#57606a" }}>New row</div>
              <div />
              <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>{preview.splitName || "—"}</div>
              <div className="tabular-nums" style={{ whiteSpace: "nowrap" }}>{formatAmountCell(preview.signedSplit)}</div>

              <div style={{ color: "#57606a", marginTop: 8 }}>Original after split</div>
              <div />
              <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>{originalName}</div>
              <div className="tabular-nums" style={{ whiteSpace: "nowrap" }}>{formatAmountCell(preview.newOriginal)}</div>
            </div>

            {!preview.valid && (
              <div style={{ marginTop: 8, color: "#664d03", background: "#fff3cd", border: "1px solid #ffecb5", padding: "6px 8px", borderRadius: 4 }}>
                Please ensure the split amount is less than the original and includes a name.
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                padding: "6px 12px",
                fontSize: 12,
                borderRadius: 4,
                border: "1px solid #dee2e6",
                background: "#fff",
                color: "#495057",
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handlePreview}
            disabled={submitting || !naturalText.trim() || !transaction}
            style={{
              padding: "6px 12px",
              fontSize: 12,
              borderRadius: 4,
              border: "1px solid #dee2e6",
              background: "#fff",
              color: "#495057",
              cursor: submitting ? "not-allowed" : "pointer",
              fontWeight: 500,
            }}
            title="Generate preview"
          >
            Preview
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting || !transaction || !preview || !preview.valid}
            style={{
              padding: "6px 14px",
              fontSize: 12,
              borderRadius: 4,
              border: "none",
              background: "#0d6efd",
              color: "#fff",
              cursor: submitting ? "not-allowed" : "pointer",
              fontWeight: 500,
            }}
            title="Save split"
          >
            {submitting ? "Saving…" : "Save split"}
          </button>
        </div>
      </div>
    </div>
  );
}

